/**
 * Pre-Key Module
 *
 * Provides generation, signing/verification, and hybrid key exchange
 * for signed pre-keys and one-time pre-keys.
 *
 * Pre-keys enable recipient-side forward secrecy:
 * - Signed pre-keys (SPK): Medium-term keys, rotated periodically, signed by identity key
 * - One-time pre-keys (OTPK): Consumed once per message, deleted after decryption
 *
 * Key exchange modes:
 * - SPK + OTPK: DH1(ephemeral, SPK.ecdh) + KEM1(SPK.kem) + DH2(ephemeral, OTPK.ecdh) + KEM2(OTPK.kem)
 * - SPK only:   DH1(ephemeral, SPK.ecdh) + KEM1(SPK.kem)
 *
 * Designed as a hybrid X3DH-equivalent, so adding a Double Ratchet later
 * is a clean additive step (the shared secret would seed the ratchet).
 *
 * @module crypto/prekeys
 */
import { x25519 } from '@noble/curves/ed25519';
import { ml_kem768, ml_kem1024 } from '@noble/post-quantum/ml-kem';
import { generateECDHKeyPair, generateKEMKeyPair } from '../keys/generate';
import { signIdentity, verifySignature } from '../sign/hybrid';
import { encrypt as symmetricEncrypt, decrypt as symmetricDecrypt } from '../encrypt/symmetric';
import { deriveKey } from '../kdf/hkdf';
import { randomBytes, concatBytes, toBytes, clearBytes } from '../utils';
// ============================================================================
// Constants
// ============================================================================
/** HKDF info string for pre-key exchange key derivation */
export const PREKEY_KDF_INFO = 'adieuu-prekey-v1';
/** Domain separator for signed pre-key signatures */
export const SPK_SIGNATURE_DOMAIN = 'adieuu-spk-v1';
/** Domain separator for v2 pre-key wrap associated data */
export const PREKEY_WRAP_AAD_DOMAIN = 'adieuu-wrap-aad-v2';
/**
 * Current pre-key wrap format version. Version 2 binds the wrap metadata
 * (pre-key IDs, ephemeral public key, KEM ciphertexts) as AEAD associated
 * data. Absent/1 = legacy wraps with no associated data.
 */
export const PREKEY_WRAP_VERSION_AAD = 2;
/**
 * Builds the associated data for a v2 pre-key session key wrap.
 *
 * Binds the pre-key IDs, ephemeral public key, and KEM ciphertexts into
 * the AEAD tag. The string header fields are newline-joined (UUIDs cannot
 * contain newlines) and the ephemeral key is fixed-length (32 bytes), so
 * the encoding is unambiguous.
 */
export function buildPreKeyWrapAad(params) {
    const header = [
        PREKEY_WRAP_AAD_DOMAIN,
        'prekey',
        params.signedPreKeyId,
        params.oneTimePreKeyId ?? '',
    ].join('\n') + '\n';
    return concatBytes(toBytes(header), params.ephemeralPublicKey, params.spkKemCiphertext, params.otpkKemCiphertext ?? new Uint8Array(0));
}
// ============================================================================
// Pre-Key Generation
// ============================================================================
/**
 * Generates a signed pre-key.
 *
 * Creates an X25519 + ML-KEM key pair and signs the public keys
 * with the identity's Ed25519 signing key. The signature proves
 * authenticity -- a compromised server cannot substitute its own SPK.
 *
 * @param signingPrivateKey - Identity's Ed25519 private key (32 bytes)
 * @param profile - Crypto profile ('default' or 'cnsa2')
 * @param pqSigningPrivateKey - ML-DSA-65 secret when hybrid identity signing is available
 * @returns Generated signed pre-key with all components
 */
export function generateSignedPreKey(signingPrivateKey, profile = 'default', pqSigningPrivateKey) {
    const keyId = crypto.randomUUID();
    const ecdh = generateECDHKeyPair();
    const kem = generateKEMKeyPair(profile);
    const dataToSign = concatBytes(toBytes(SPK_SIGNATURE_DOMAIN), toBytes(keyId), ecdh.publicKey, kem.publicKey);
    const signature = signIdentity(signingPrivateKey, dataToSign, pqSigningPrivateKey);
    return { keyId, ecdh, kem, signature };
}
/**
 * Verifies a signed pre-key's signature.
 *
 * Senders MUST call this before using a signed pre-key to prevent
 * a compromised server from substituting a malicious key.
 *
 * @param spk - Signed pre-key public components
 * @param signingPublicKey - Identity's Ed25519 public key (32 bytes)
 * @param pqSigningPublicKey - ML-DSA-65 public key when the SPK signature is hybrid
 * @returns true if signature is valid
 */
export function verifySignedPreKey(spk, signingPublicKey, pqSigningPublicKey) {
    const dataToVerify = concatBytes(toBytes(SPK_SIGNATURE_DOMAIN), toBytes(spk.keyId), spk.ecdhPublicKey, spk.kemPublicKey);
    return verifySignature(signingPublicKey, dataToVerify, spk.signature, pqSigningPublicKey);
}
/**
 * Generates a batch of one-time pre-keys.
 *
 * Each OTPK is consumed exactly once by a sender. After the recipient
 * decrypts the message, the OTPK private key should be deleted to
 * achieve forward secrecy.
 *
 * @param count - Number of OTPKs to generate
 * @param profile - Crypto profile
 * @returns Array of generated one-time pre-keys
 */
export function generateOneTimePreKeys(count, profile = 'default') {
    const keys = [];
    for (let i = 0; i < count; i++) {
        keys.push({
            keyId: crypto.randomUUID(),
            ecdh: generateECDHKeyPair(),
            kem: generateKEMKeyPair(profile),
        });
    }
    return keys;
}
// ============================================================================
// Pre-Key Exchange (Sender Side)
// ============================================================================
/**
 * Performs a hybrid pre-key exchange (sender side).
 *
 * Combines X25519 ECDH and ML-KEM with the recipient's pre-keys
 * to derive a shared secret. When both SPK and OTPK are available:
 *
 *   DH1 = X25519(ephemeral, SPK.ecdh)
 *   KEM1 = ML-KEM.Encapsulate(SPK.kem)
 *   DH2 = X25519(ephemeral, OTPK.ecdh)
 *   KEM2 = ML-KEM.Encapsulate(OTPK.kem)
 *   sharedSecret = HKDF(DH1 || KEM1 || DH2 || KEM2, info="adieuu-prekey-v1")
 *
 * When only SPK is available:
 *   sharedSecret = HKDF(DH1 || KEM1, info="adieuu-prekey-v1")
 *
 * @param signedPreKey - Recipient's signed pre-key public components
 * @param oneTimePreKey - Recipient's one-time pre-key (optional)
 * @param profile - Crypto profile
 * @returns Exchange result with shared secret and ciphertexts
 */
export function preKeyExchange(signedPreKey, oneTimePreKey, profile = 'default') {
    const kem = profile === 'cnsa2' ? ml_kem1024 : ml_kem768;
    const ephemeralPrivate = randomBytes(32);
    const ephemeralPublicKey = x25519.getPublicKey(ephemeralPrivate);
    // DH1: ephemeral <-> SPK ECDH
    const dh1 = x25519.getSharedSecret(ephemeralPrivate, signedPreKey.ecdhPublicKey);
    // KEM1: encapsulate with SPK KEM
    const kem1Result = kem.encapsulate(signedPreKey.kemPublicKey);
    const spkKemCiphertext = kem1Result.cipherText;
    const kem1Shared = kem1Result.sharedSecret;
    let ikm;
    let otpkKemCiphertext;
    if (oneTimePreKey) {
        // DH2: ephemeral <-> OTPK ECDH
        const dh2 = x25519.getSharedSecret(ephemeralPrivate, oneTimePreKey.ecdhPublicKey);
        // KEM2: encapsulate with OTPK KEM
        const kem2Result = kem.encapsulate(oneTimePreKey.kemPublicKey);
        otpkKemCiphertext = kem2Result.cipherText;
        const kem2Shared = kem2Result.sharedSecret;
        ikm = concatBytes(dh1, kem1Shared, dh2, kem2Shared);
        clearBytes(dh2);
        clearBytes(kem2Shared);
    }
    else {
        ikm = concatBytes(dh1, kem1Shared);
    }
    const sharedSecret = deriveKey({ ikm, info: PREKEY_KDF_INFO, length: 32 }, profile);
    // Zeroize the ephemeral private key and intermediate secrets: only the
    // derived shared secret leaves this function.
    clearBytes(ephemeralPrivate);
    clearBytes(dh1);
    clearBytes(kem1Shared);
    clearBytes(ikm);
    return {
        sharedSecret,
        ephemeralPublicKey,
        spkKemCiphertext,
        otpkKemCiphertext,
    };
}
/**
 * Wraps a session key using pre-key exchange.
 *
 * This is the pre-key-aware replacement for `wrapSessionKey()` in the
 * hybrid encryption module.
 *
 * @param sessionKey - Random session key to wrap (32 bytes)
 * @param signedPreKey - Recipient's signed pre-key
 * @param oneTimePreKey - Recipient's one-time pre-key (optional)
 * @param profile - Crypto profile
 * @returns Wrapped key with all ciphertexts needed for decapsulation
 */
export function wrapSessionKeyWithPreKeys(sessionKey, signedPreKey, oneTimePreKey, profile = 'default') {
    if (sessionKey.length !== 32) {
        throw new Error('Session key must be 32 bytes');
    }
    const exchange = preKeyExchange(signedPreKey, oneTimePreKey, profile);
    const aad = buildPreKeyWrapAad({
        signedPreKeyId: signedPreKey.keyId,
        oneTimePreKeyId: oneTimePreKey?.keyId,
        ephemeralPublicKey: exchange.ephemeralPublicKey,
        spkKemCiphertext: exchange.spkKemCiphertext,
        otpkKemCiphertext: exchange.otpkKemCiphertext,
    });
    const { ciphertext: wrappedSessionKey, nonce: wrappingNonce } = symmetricEncrypt(exchange.sharedSecret, sessionKey, profile, undefined, aad);
    clearBytes(exchange.sharedSecret);
    return {
        ephemeralPublicKey: exchange.ephemeralPublicKey,
        spkKemCiphertext: exchange.spkKemCiphertext,
        otpkKemCiphertext: exchange.otpkKemCiphertext,
        wrappedSessionKey,
        wrappingNonce,
        signedPreKeyId: signedPreKey.keyId,
        oneTimePreKeyId: oneTimePreKey?.keyId,
        wrapVersion: PREKEY_WRAP_VERSION_AAD,
    };
}
// ============================================================================
// Pre-Key Decapsulation (Recipient Side)
// ============================================================================
/**
 * Decapsulates a pre-key exchange (recipient side).
 *
 * Reverses the sender's pre-key exchange to derive the same shared secret.
 *
 * @param senderEphemeralPublic - Sender's ephemeral X25519 public key
 * @param spkEcdhPrivate - Recipient's SPK X25519 private key
 * @param spkKemPrivate - Recipient's SPK ML-KEM private key
 * @param spkKemCiphertext - KEM ciphertext for SPK
 * @param otpkEcdhPrivate - Recipient's OTPK X25519 private key (optional)
 * @param otpkKemPrivate - Recipient's OTPK ML-KEM private key (optional)
 * @param otpkKemCiphertext - KEM ciphertext for OTPK (optional)
 * @param profile - Crypto profile
 * @returns Derived shared secret (same as sender computed)
 */
export function preKeyDecapsulate(senderEphemeralPublic, spkEcdhPrivate, spkKemPrivate, spkKemCiphertext, otpkEcdhPrivate, otpkKemPrivate, otpkKemCiphertext, profile = 'default') {
    const kemAlgo = profile === 'cnsa2' ? ml_kem1024 : ml_kem768;
    // DH1: SPK ECDH
    const dh1 = x25519.getSharedSecret(spkEcdhPrivate, senderEphemeralPublic);
    // KEM1: decapsulate SPK KEM
    const kem1Shared = kemAlgo.decapsulate(spkKemCiphertext, spkKemPrivate);
    let ikm;
    if (otpkEcdhPrivate && otpkKemPrivate && otpkKemCiphertext) {
        // DH2: OTPK ECDH
        const dh2 = x25519.getSharedSecret(otpkEcdhPrivate, senderEphemeralPublic);
        // KEM2: decapsulate OTPK KEM
        const kem2Shared = kemAlgo.decapsulate(otpkKemCiphertext, otpkKemPrivate);
        ikm = concatBytes(dh1, kem1Shared, dh2, kem2Shared);
        clearBytes(dh2);
        clearBytes(kem2Shared);
    }
    else {
        ikm = concatBytes(dh1, kem1Shared);
    }
    const sharedSecret = deriveKey({ ikm, info: PREKEY_KDF_INFO, length: 32 }, profile);
    // Zeroize intermediate secrets.
    clearBytes(dh1);
    clearBytes(kem1Shared);
    clearBytes(ikm);
    return sharedSecret;
}
/**
 * Unwraps a session key using pre-key decapsulation.
 *
 * This is the pre-key-aware replacement for `unwrapSessionKey()`.
 *
 * @param wrapped - Wrapped key from sender
 * @param spkEcdhPrivate - Recipient's SPK X25519 private key
 * @param spkKemPrivate - Recipient's SPK ML-KEM private key
 * @param otpkEcdhPrivate - Recipient's OTPK X25519 private key (optional)
 * @param otpkKemPrivate - Recipient's OTPK ML-KEM private key (optional)
 * @param profile - Crypto profile
 * @returns Unwrapped session key (32 bytes)
 */
export function unwrapSessionKeyWithPreKeys(wrapped, spkEcdhPrivate, spkKemPrivate, otpkEcdhPrivate, otpkKemPrivate, profile = 'default') {
    const sharedSecret = preKeyDecapsulate(wrapped.ephemeralPublicKey, spkEcdhPrivate, spkKemPrivate, wrapped.spkKemCiphertext, otpkEcdhPrivate, otpkKemPrivate, wrapped.otpkKemCiphertext, profile);
    // v2 wraps authenticate the wrap metadata as associated data; legacy
    // wraps (no version) used no associated data.
    let aad;
    if (wrapped.wrapVersion === PREKEY_WRAP_VERSION_AAD) {
        if (!wrapped.signedPreKeyId) {
            throw new Error('signedPreKeyId required to unwrap a v2 pre-key wrap');
        }
        aad = buildPreKeyWrapAad({
            signedPreKeyId: wrapped.signedPreKeyId,
            oneTimePreKeyId: wrapped.oneTimePreKeyId,
            ephemeralPublicKey: wrapped.ephemeralPublicKey,
            spkKemCiphertext: wrapped.spkKemCiphertext,
            otpkKemCiphertext: wrapped.otpkKemCiphertext,
        });
    }
    try {
        return symmetricDecrypt(sharedSecret, wrapped.wrappedSessionKey, wrapped.wrappingNonce, profile, aad);
    }
    finally {
        clearBytes(sharedSecret);
    }
}
//# sourceMappingURL=index.js.map